import React, { useState, useEffect, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Check, Copy, ExternalLink, Loader2, MoreVertical, RotateCcw, Save, SquarePen, SquarePlus, Trash2 } from 'lucide-react';
import PropTypes from 'prop-types';

import API from '@/apiRequest';
import Modal from '@/components/common/Modal';
import { BASE_URL } from '@/config';
import ModalConfirmChanges from '@/orderDetails/ModalConfirmChanges';
import { showToast } from '@/store/ducks/toasts';
import InspectionIcon from '@/svg-icons/inspections';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAsyncService } from '@/contexts/AsyncServiceContext';

const InspectionEditRequestsSection = ({ orderId, refreshHandler }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { on, off } = useAsyncService();

  const [renewOpen, setRenewOpen] = useState(false);
  const [renewToken, setRenewToken] = useState(null);
  const [extraHours, setExtraHours] = useState('');
  const [extraHoursError, setExtraHoursError] = useState('');
  const renewInputRef = useRef(null);

  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflictToken, setConflictToken] = useState(null);
  const [conflictData, setConflictData] = useState({
    current: null,
    updated: null,
    originalUpdated: null,
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteToken, setDeleteToken] = useState(null);
  const [applyLoadingToken, setApplyLoadingToken] = useState(null);
  const [actionMenuToken, setActionMenuToken] = useState(null);
  const actionMenuRef = useRef(null);

  const {
    data: requests = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['inspectionEditRequests', orderId],
    queryFn: async () => {
      const res = await API.get(`orders/${orderId}/inspection-edit-requests`);
      return res.data || [];
    },
    enabled: !!orderId,
  });

  useEffect(() => {
    const handleNotification = notification => {
      if (notification?.type === 'INSPECTION_EDIT.UPDATED') {
        const orderPublicId = notification?.data?.order_public_id;
        if (orderPublicId === orderId) {
          refetch();
        }
      }
    };

    on('notification', handleNotification);
    return () => {
      off('notification', handleNotification);
    };
  }, [on, off, orderId, refetch]);

  const createAutoMutation = useMutation({
    mutationFn: () => API.post(`orders/${orderId}/inspection-edit-requests`, { ttl_hours: 48 }),
    onSuccess: res => {
      refetch();
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'toast.success.title' }),
          text: intl.formatMessage({ id: 'orderDetails.editRequests.createSuccess' }),
          duration: 5000,
        })
      );

      const token = res?.data?.token;
      if (token) {
        openEditInNewTab(token);
      }
    },
    onError: err => {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: err?.response?.data?.message || intl.formatMessage({ id: 'UNKNOWN_ERROR' }),
          duration: 5000,
        })
      );
    },
  });

  const renewMutation = useMutation({
    mutationFn: ({ token, extra }) => API.patch(`orders/inspection-edit-requests/${token}`, { extra_hours: extra }),
    onSuccess: () => {
      setRenewOpen(false);
      setRenewToken(null);
      setExtraHours('');
      refetch();
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'toast.success.title' }),
          text: intl.formatMessage({ id: 'orderDetails.editRequests.renewSuccess' }),
          duration: 5000,
        })
      );
    },
    onError: err => {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: err?.response?.data?.message || intl.formatMessage({ id: 'UNKNOWN_ERROR' }),
          duration: 5000,
        })
      );
    },
  });

  const applyMutation = useMutation({
    mutationFn: ({ token, force, updatedData }) => {
      const body = force ? { force: true } : {};
      if (updatedData) {
        body.updated_data = updatedData;
      }
      return API.post(`orders/inspection-edit-requests/${token}/commit`, body);
    },
    onSuccess: () => {
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'toast.success.title' }),
          text: intl.formatMessage({ id: 'orderDetails.editRequests.applySuccess' }),
          duration: 5000,
        })
      );
      if (refreshHandler) refreshHandler();
      refetch();
      setConflictOpen(false);
      setConflictToken(null);
      setConflictData({
        current: null,
        updated: null,
        originalUpdated: null,
      });
      setApplyLoadingToken(null);
    },
    onError: (err, variables) => {
      if (err?.response?.status === 409) {
        const payload = err.response.data || {};
        if (payload.current_data && payload.updated_data) {
          setConflictData({
            current: payload.current_data,
            updated: payload.updated_data,
            originalUpdated: JSON.parse(JSON.stringify(payload.updated_data)),
          });
          setConflictToken(variables.token);
          setConflictOpen(true);
          return;
        }
      }
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: err?.response?.data?.message || intl.formatMessage({ id: 'UNKNOWN_ERROR' }),
          duration: 5000,
        })
      );
      setApplyLoadingToken(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: token => API.delete(`orders/inspection-edit-requests/${token}`),
    onSuccess: () => {
      setDeleteOpen(false);
      setDeleteToken(null);
      refetch();
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'toast.success.title' }),
          text: intl.formatMessage({ id: 'orderDetails.editRequests.deleteSuccess' }),
          duration: 5000,
        })
      );
    },
  });

  const openEditInNewTab = token => {
    if (!token) return;

    const accessToken = sessionStorage.getItem('accessToken') || '';
    const tokenType = sessionStorage.getItem('tokenType') || '';
    const url = `${BASE_URL}/inspection-web/edit/${token}#access_token=${accessToken}&token_type=${tokenType}&expires_in=`;
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
    }
  };

  const copyTokenToClipboard = async token => {
    if (!token) return;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(token);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = token;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'toast.success.title' }),
          text: intl.formatMessage({ id: 'orderDetails.editRequests.copyTokenSuccess' }),
          duration: 5000,
        })
      );
    } catch (err) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'UNKNOWN_ERROR' }),
          duration: 5000,
        })
      );
    }
  };

  const handleRenew = e => {
    if (e && e.preventDefault) e.preventDefault();
    const extra = parseInt(extraHours, 10);
    if (!extra || extra <= 0) {
      const msg = intl.formatMessage({ id: 'orderDetails.editRequests.extraHoursPlaceholder' });
      setExtraHoursError(msg);
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: msg,
          duration: 5000,
        })
      );
      if (renewInputRef?.current && typeof renewInputRef.current.focus === 'function') {
        try {
          renewInputRef.current.focus();
        } catch (err) {}
      }
      return;
    }

    setExtraHoursError('');
    renewMutation.mutate({ token: renewToken, extra });
  };

  useEffect(() => {
    if (renewOpen && renewInputRef?.current && typeof renewInputRef.current.focus === 'function') {
      try {
        renewInputRef.current.focus();
      } catch (err) {}
    }
  }, [renewOpen]);

  useEffect(() => {
    const closeOnClickAway = evt => {
      if (actionMenuRef?.current?.contains(evt.target)) return;
      setActionMenuToken(null);
    };

    const closeOnEsc = evt => {
      if (evt.key !== 'Escape') return;
      setActionMenuToken(null);
    };

    document.addEventListener('mouseup', closeOnClickAway);
    document.addEventListener('keyup', closeOnEsc);

    return () => {
      document.removeEventListener('mouseup', closeOnClickAway);
      document.removeEventListener('keyup', closeOnEsc);
    };
  }, []);

  const actionLoading =
    createAutoMutation.isPending || renewMutation.isPending || applyMutation.isPending || deleteMutation.isPending;

  const formatDateTime = value => (value ? new Date(value).toLocaleString() : '');

  const getRequestOwner = request =>
    request.allowed_users
      ?.map(user => user.name || user.username)
      .filter(Boolean)
      .join(', ') || '';

  const hasPendingChanges = request => {
    // Ensure the base date fields exist
    if (!request.updated_at || !request.created_at) return false;

    const updated = new Date(request.updated_at);
    const created = new Date(request.created_at);

    // 1. updated_at must be greater than created_at
    if (updated <= created) return false;

    // 2. If committed_at exists, updated_at must be greater than it
    if (request.committed_at) {
      const committed = new Date(request.committed_at);
      return updated > committed;
    }

    // If it was updated after creation and has no commit yet (null), there are pending changes
    return true;
  };

  const sectionTextClass = 'font-bold text-[calc(6px+0.4vw)] my-[0.6vw] text-[#222]';

  return (
    <div className="col-span-12 mb-4">
      <div className="bg-white">
        <div className={`flex items-center ${sectionTextClass}`}>
          <span className="mr-[0.4vw] flex w-[calc(14px+0.6vw)] h-[calc(14px+0.6vw)] shrink-0 items-center justify-center rounded-full border-[1.5px] border-gray-700 text-gray-700 align-middle">
            <SquarePen className="h-[58%] w-[58%]" strokeWidth={2.4} />
          </span>
          <FormattedMessage id="orderDetails.editRequests.header" />
        </div>

        <p className="mb-0 mt-[1vw] max-w-[720px] pl-[calc(14px+1.8vw)] text-[calc(6px+0.4vw)] text-[#858585] leading-normal">
          <FormattedMessage id="orderDetails.editRequests.description" />{' '}
          <a
            href="https://s.vistori.as/manual_edicao_web_vistorias.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold !text-[#5f6670] underline"
          >
            <FormattedMessage id="orderDetails.editRequests.learnMore" />
          </a>
        </p>

        <div className="mt-[1vw] pl-[calc(14px+1.8vw)]">
          {isLoading ? (
            <div className="flex justify-center p-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : requests.length === 0 ? (
            <div className="flex min-h-[50px] items-center justify-between gap-4 rounded-[6px] bg-[#e7e7ea] px-[12px] py-[8px]">
              <div className="flex min-w-0 items-center gap-2 text-[calc(6px+0.4vw)] font-semibold text-[#858585]">
                <Check className="h-[18px] w-[18px] shrink-0 text-[#858585]" strokeWidth={2.5} />
                <FormattedMessage id="orderDetails.editRequests.noRequests" />
              </div>

              <Button
                type="button"
                className="h-[34px] shrink-0 !rounded-[4px] bg-[#1f2937] px-[12px] text-[calc(6px+0.4vw)] font-semibold text-white shadow-none hover:bg-[#111827]"
                disabled={actionLoading}
                onClick={() => createAutoMutation.mutate()}
              >
                {createAutoMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <SquarePlus className="mr-2 h-[16px] w-[16px]" />
                    <FormattedMessage id="orderDetails.editRequests.newSession" />
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div>
              <div className="mb-[1vw] text-[calc(6px+0.4vw)] font-semibold text-[#858585]">
                <FormattedMessage id="orderDetails.editRequests.activeSection" />
              </div>
              <ul className="list-none! m-0! p-0!">
                {requests.map(r => {
                  const isExpired = r.expires_at ? new Date(r.expires_at) < new Date() : false;
                  const requestHasChanges = hasPendingChanges(r);
                  const requestOwner = getRequestOwner(r);
                  const saveDisabled = actionLoading || !requestHasChanges || isExpired;

                  return (
                    <li
                      key={r.token}
                      className="flex min-h-[50px] items-center justify-start rounded-[6px] bg-[#e7e7ea] px-[12px] py-[8px]"
                    >
                      <SquarePen className="mr-[10px] h-[20px] w-[20px] shrink-0 text-[#7b8490]" strokeWidth={2.6} />

                      <div className="min-w-0 flex-1 pr-4">
                        <div className="truncate text-[calc(6px+0.4vw)] font-bold leading-normal text-[#1f2937]">
                          {requestOwner || <FormattedMessage id="orderDetails.editRequests.unknownUser" />}
                        </div>
                        <div className="mt-[1px] text-[calc(5px+0.35vw)] leading-normal text-[#1f2937]">
                          <FormattedMessage
                            id={isExpired ? "orderDetails.editRequests.expiredAt" : "orderDetails.editRequests.expiresAt"}
                            values={{ date: formatDateTime(r.expires_at) }}
                          />
                        </div>
                      </div>

                      <div className="flex min-h-[34px] shrink-0 items-center gap-[12px]">
                        <Button
                          type="button"
                          className={`h-[34px] w-[34px] !rounded-[4px] p-0 shadow-none ${
                            saveDisabled
                              ? 'bg-[#aeb4bc] text-white hover:bg-[#aeb4bc]'
                              : 'bg-[#1f2937] text-white hover:bg-[#111827]'
                          }`}
                          disabled={saveDisabled}
                          title={intl.formatMessage({ id: 'orderDetails.editRequests.apply' })}
                          onClick={() => {
                            setApplyLoadingToken(r.token);
                            applyMutation.mutate({ token: r.token });
                          }}
                        >
                          {applyMutation.isPending && applyLoadingToken === r.token ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="h-[17px] w-[17px]" strokeWidth={2.4} />
                          )}
                        </Button>

                        <Button
                          type="button"
                          className={`h-[34px] w-[34px] !rounded-[4px] p-0 text-white shadow-none ${
                            actionLoading || isExpired
                              ? 'bg-[#aeb4bc] hover:bg-[#aeb4bc]'
                              : 'bg-[#1f2937] hover:bg-[#111827]'
                          }`}
                          title={intl.formatMessage({ id: 'orderDetails.editRequests.openEditorTooltip' })}
                          onClick={() => openEditInNewTab(r.token)}
                          disabled={actionLoading || isExpired}
                        >
                          <ExternalLink className="h-[17px] w-[17px]" strokeWidth={2.4} />
                        </Button>

                        <div className="relative" ref={actionMenuToken === r.token ? actionMenuRef : null}>
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-[34px] w-[28px] rounded-[4px] p-0 text-[#1f2937] shadow-none hover:bg-black/5"
                            title={intl.formatMessage({ id: 'orderDetails.editRequests.actionsTooltip' })}
                            onClick={() => setActionMenuToken(prev => (prev === r.token ? null : r.token))}
                            disabled={actionLoading}
                          >
                            <MoreVertical className="h-[19px] w-[19px]" strokeWidth={2.6} />
                          </Button>

                          {actionMenuToken === r.token && (
                            <div className="absolute right-0 top-full z-[20] mt-[6px] w-[242px] overflow-hidden rounded-[4px] bg-white py-[6px] shadow-[0_2px_8px_rgba(0,0,0,0.24)]">
                              <button
                                type="button"
                                className="flex h-[38px] w-full items-center gap-[12px] border-0 bg-transparent px-[14px] text-left text-[13px] text-[#4b5563] hover:bg-[#f3f4f6]"
                                onClick={() => {
                                  setActionMenuToken(null);
                                  copyTokenToClipboard(r.token);
                                }}
                              >
                                <Copy className="h-[17px] w-[17px] shrink-0 text-[#1f2937]" strokeWidth={2.4} />
                                <FormattedMessage id="orderDetails.editRequests.copyTokenTooltip" />
                              </button>

                              <button
                                type="button"
                                className="flex h-[38px] w-full items-center gap-[12px] border-0 bg-transparent px-[14px] text-left text-[13px] text-[#4b5563] hover:bg-[#f3f4f6]"
                                onClick={() => {
                                  setActionMenuToken(null);
                                  setRenewToken(r.token);
                                  const expiresAt = r.expires_at ? new Date(r.expires_at) : null;
                                  if (expiresAt) {
                                    const now = new Date();
                                    const target = new Date(now.getTime() + 24 * 60 * 60 * 1000);
                                    const diffMs = target.getTime() - expiresAt.getTime();
                                    const diffHours = Math.ceil(diffMs / (60 * 60 * 1000));
                                    setExtraHours(diffHours > 0 ? String(diffHours) : '24');
                                  } else {
                                    setExtraHours('24');
                                  }
                                  setRenewOpen(true);
                                }}
                              >
                                <RotateCcw className="h-[17px] w-[17px] shrink-0 text-[#1f2937]" strokeWidth={2.4} />
                                <FormattedMessage id="orderDetails.editRequests.renew" />
                              </button>

                              <button
                                type="button"
                                className="flex h-[38px] w-full items-center gap-[12px] border-0 bg-transparent px-[14px] text-left text-[13px] text-[#4b5563] hover:bg-[#f3f4f6]"
                                onClick={() => {
                                  setActionMenuToken(null);
                                  setDeleteToken(r.token);
                                  setDeleteOpen(true);
                                }}
                              >
                                <Trash2 className="h-[17px] w-[17px] shrink-0 text-[#1f2937]" strokeWidth={2.4} />
                                <FormattedMessage id="orderDetails.editRequests.endSection" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      {renewOpen && (
        <Modal
          onClose={() => setRenewOpen(false)}
          extraStyles={{ maxWidth: '80%', width: '480px', minWidth: '480px', padding: undefined }}
        >
          <form
            onSubmit={handleRenew}
            role="dialog"
            aria-modal="true"
            aria-labelledby="renew-modal-title"
            className="w-full max-w-[422px]"
            style={{ margin: '0 auto' }}
          >
            <div id="renew-modal-title" className="flex items-center gap-2 text-[18px] font-semibold mb-4">
              <InspectionIcon className="w-[18px] h-[18px]" />
              <span>
                <FormattedMessage id="orderDetails.editRequests.renew" />
              </span>
            </div>
            <hr className="my-4 border-[#E9E9E9]" />

            <div className="my-4 relative">
              <Label htmlFor="renew-extra-hours" className="text-sm text-gray-700 block mb-2">
                {intl.formatMessage({ id: 'orderDetails.editRequests.extraHoursPlaceholder' })}
              </Label>
              <div
                className="flex items-center w-[422px] h-[32px]"
                style={{ fontFamily: 'Roboto, Helvetica, Arial, sans-serif', fontSize: '16px' }}
              >
                <input
                  id="renew-extra-hours"
                  ref={renewInputRef}
                  type="number"
                  min={1}
                  step={1}
                  value={extraHours}
                  onChange={e => {
                    setExtraHours(e.target.value);
                    if (extraHoursError) setExtraHoursError('');
                  }}
                  aria-describedby={extraHoursError ? 'renew-extra-error' : undefined}
                  aria-invalid={!!extraHoursError}
                  inputMode="numeric"
                  style={{
                    backgroundColor: 'rgba(0,0,0,0)',
                    border: 'none',
                    boxSizing: 'content-box',
                    caretColor: 'rgba(0,0,0,0.87)',
                    color: 'rgba(0,0,0,0.87)',
                    display: 'block',
                    flexGrow: 1,
                    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                    fontSize: '16px',
                    height: '19px',
                    lineHeight: '19px',
                    paddingTop: '6px',
                    paddingBottom: '7px',
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    width: '100%',
                    outline: 'none',
                  }}
                />
              </div>
              {extraHoursError && (
                <div id="renew-extra-error" className="text-sm text-red-600 mt-1">
                  {extraHoursError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  setRenewOpen(false);
                  setExtraHoursError('');
                }}
                disabled={renewMutation.isPending}
                className="min-w-[140px] h-[36px] text-[15px] font-bold px-4 rounded-[3px] bg-[#c1c1c1] text-white hover:opacity-90 transition-opacity"
              >
                <FormattedMessage id="BUTTON.CANCEL" />
              </Button>
              <Button
                type="submit"
                disabled={renewMutation.isPending}
                className="min-w-[140px] h-[36px] text-[15px] font-bold px-4 rounded-[3px] bg-[#97a41f] text-white hover:opacity-90 transition-opacity"
              >
                {renewMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                <FormattedMessage id="BUTTON.SAVE" />
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {conflictOpen && conflictData.updated && (
        <Modal onClose={() => setConflictOpen(false)}>
          <ModalConfirmChanges
            onClose={() => setConflictOpen(false)}
            currentData={conflictData.current}
            updatedData={conflictData.updated}
            originalUpdatedData={conflictData.originalUpdated}
            changeUpdatedData={(key, value) =>
              setConflictData(prev => {
                if (!prev.updated) return prev;
                return {
                  ...prev,
                  updated: {
                    ...prev.updated,
                    details: {
                      ...(prev.updated.details || {}),
                      [key]: value,
                    },
                  },
                };
              })
            }
            makeOfficialHandler={(token, force, rejected) =>
              applyMutation.mutate({
                token: token || conflictToken,
                force,
                updatedData: rejected ? conflictData.updated : null,
              })
            }
            inspectionId={conflictToken}
            loading={applyMutation.isPending}
          />
        </Modal>
      )}

      {deleteOpen && (
        <Modal
          onClose={() => setDeleteOpen(false)}
          extraStyles={{ maxWidth: '80%', width: '480px', minWidth: '480px' }}
        >
          <div className="max-w-[422px] mx-auto">
            <div className="flex items-center gap-2 font-bold text-lg mb-4">
              <InspectionIcon className="size-[18px]" />
              <FormattedMessage id="DELETE" />
            </div>
            <hr className="my-4 border-[#E9E9E9]" />
            <div className="my-4">
              <p>
                <FormattedMessage id="MESSAGE.DELETE_CONFIRMATION" />
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setDeleteOpen(false)}
                disabled={actionLoading}
                className="min-w-[140px] h-[36px] text-[15px] font-bold px-4 rounded-[3px] bg-[#c1c1c1] text-white hover:opacity-90 transition-opacity"
              >
                <FormattedMessage id="BUTTON.CANCEL" />
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteMutation.mutate(deleteToken)}
                disabled={actionLoading}
                className="min-w-[140px] h-[36px] text-[15px] font-bold px-4 rounded-[3px] bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                <FormattedMessage id="DELETE" />
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

InspectionEditRequestsSection.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  refreshHandler: PropTypes.func,
};

InspectionEditRequestsSection.defaultProps = {
  refreshHandler: () => {},
};

export default InspectionEditRequestsSection;
